import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Award, Star, Package, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';

const QualitaPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#e8f0e8] to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#1a5c3a] mb-4 block">
              Il nostro impegno
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1a3d2a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Qualità <span className="italic">Garantita</span>
            </h1>
            <p className="text-xl text-[#4a7a5a] font-light max-w-2xl mx-auto">
              Ogni prodotto su QANTRI è selezionato con cura per garantirti la migliore esperienza d'acquisto.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-neutral-100 mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Come <span className="italic">selezioniamo</span> i prodotti
            </h2>
            
            <p className="text-neutral-600 text-lg font-light leading-relaxed mb-6">
              Non vendiamo tutto. Ogni prodotto presente su QANTRI passa attraverso un processo di selezione rigoroso. Collaboriamo solo con fornitori affidabili che rispettano i nostri standard di qualità.
            </p>
            
            <p className="text-neutral-600 text-lg font-light leading-relaxed">
              I nostri criteri di selezione includono: qualità dei materiali, recensioni dei clienti, affidabilità del fornitore, e rapporto qualità-prezzo. Se un prodotto non ci convince, non lo troverai nel nostro catalogo.
            </p>
          </motion.div>

          {/* Quality Points */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              {
                icon: Shield,
                title: 'Fornitori Verificati',
                desc: 'Collaboriamo solo con fornitori che hanno dimostrato affidabilità nel tempo. Ogni partner viene valutato prima di entrare nel nostro network.'
              },
              {
                icon: Star,
                title: 'Prodotti Selezionati',
                desc: 'Non tutto arriva sul nostro catalogo. Scegliamo solo prodotti che rispettano i nostri standard di qualità e design.'
              },
              {
                icon: Package,
                title: 'Imballaggio Sicuro',
                desc: 'Ogni ordine viene imballato con cura per garantire che arrivi nelle migliori condizioni possibili.'
              },
              {
                icon: Award,
                title: 'Garanzia Soddisfazione',
                desc: 'Se non sei soddisfatto del tuo acquisto, il nostro team di assistenza è pronto ad aiutarti con resi e rimborsi.'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100"
              >
                <div className="w-12 h-12 rounded-full bg-[#1a5c3a]/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-[#1a5c3a]" />
                </div>
                <h3 className="font-medium text-[#1a3d2a] mb-2 text-lg">{item.title}</h3>
                <p className="text-neutral-500 font-light">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-16 md:py-20 bg-[#1a5c3a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Truck className="h-12 w-12 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Spedizioni <span className="italic">Affidabili</span>
            </h2>
            <p className="text-white/80 text-lg font-light leading-relaxed max-w-2xl mx-auto mb-4">
              Spediamo da magazzini in Italia e Europa per garantirti consegne rapide e tracciabili. La maggior parte degli ordini arriva in 24-48 ore lavorative.
            </p>
            <p className="text-white/60 font-light">
              Spedizione gratuita per ordini sopra €50
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <CheckCircle className="h-10 w-10 text-[#1a5c3a] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Pronto a scoprire la <span className="italic">differenza</span>?
            </h2>
            <p className="text-neutral-600 font-light mb-8">
              Esplora il nostro catalogo di prodotti selezionati con cura.
            </p>
            <Link to="/products">
              <Button className="h-14 px-8 text-sm uppercase tracking-[0.15em] font-medium bg-[#1a5c3a] text-white hover:bg-[#2d7a4a] rounded-full transition-all">
                Scopri i prodotti
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QualitaPage;
